import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { PlusIcon, TrashIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { homepageService } from '../services/homepage';
import { dressService, Dress } from '../services/dress';
import { HomepageDressEntry } from '../types/homepage';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { useToast } from '../contexts/ToastContext';

type Section = 'style_matcher' | 'collection';

const SECTION_CONFIG: Record<Section, { title: string; description: string; maxItems: number | null }> = {
  style_matcher: {
    title: 'Style Matcher Preview',
    description: 'Dresses displayed in the "Discover Your Style with Our Smart Matcher" section on the homepage. No limit on number of dresses.',
    maxItems: null,
  },
  collection: {
    title: 'Exclusive Collection',
    description: 'Dresses displayed in the "From Our Exclusive Collection" section on the homepage. Maximum 6 dresses.',
    maxItems: 6,
  },
};

const HomepageDressesManagement: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>('style_matcher');
  const [entries, setEntries] = useState<HomepageDressEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragDisabled, setDragDisabled] = useState(false);
  const { showSuccess, showError } = useToast();

  // Add dress state
  const [showAddSection, setShowAddSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Dress[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedDressId, setSelectedDressId] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadEntries();
  }, [activeSection]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await homepageService.getHomepageDresses(activeSection);
      setEntries(data);
    } catch (err: any) {
      console.error('Failed to load homepage dresses:', err);
      showError('Failed to load homepage dresses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const result = await dressService.getDresses({ search: searchQuery.trim(), limit: 20 });
      // Filter out dresses already in this section
      const existingIds = new Set(entries.map(e => e.dressId));
      setSearchResults(result.data.filter(d => !existingIds.has(d.id)));
    } catch (err) {
      console.error('Search failed:', err);
      showError('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddDress = async () => {
    if (!selectedDressId) {
      showError('Please select a dress');
      return;
    }

    setAdding(true);
    try {
      await homepageService.addHomepageDress({
        dressId: selectedDressId,
        section: activeSection,
        isActive: true,
      });
      await loadEntries();
      showSuccess('Dress added to homepage section');
      setShowAddSection(false);
      setSelectedDressId('');
      setSearchQuery('');
      setSearchResults([]);
    } catch (err: any) {
      console.error('Failed to add dress:', err);
      showError(err.response?.data?.error || 'Failed to add dress');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entry: HomepageDressEntry) => {
    if (!window.confirm(`Remove "${entry.dress.name}" from this section?`)) return;

    try {
      await homepageService.removeHomepageDress(entry.id);
      await loadEntries();
      showSuccess('Dress removed');
    } catch (err) {
      console.error('Failed to remove dress:', err);
      showError('Failed to remove dress');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || dragDisabled) return;
    if (result.source.index === result.destination.index) return;

    const updated = Array.from(entries);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    const withOrder = updated.map((entry, index) => ({
      ...entry,
      displayOrder: index,
    }));

    setEntries(withOrder);
    setDragDisabled(true);

    try {
      await homepageService.updateHomepageDressesOrder(
        activeSection,
        withOrder.map((e, i) => ({ id: e.id, displayOrder: i }))
      );
      showSuccess('Order updated');
    } catch (err) {
      console.error('Failed to update order:', err);
      showError('Failed to update order');
      await loadEntries();
    } finally {
      setDragDisabled(false);
    }
  };

  const config = SECTION_CONFIG[activeSection];
  const canAddMore = config.maxItems === null || entries.length < config.maxItems;

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Homepage Dresses</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage which dresses appear in each homepage section. Drag to reorder.
          </p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(Object.keys(SECTION_CONFIG) as Section[]).map((section) => (
            <button
              key={section}
              onClick={() => {
                setActiveSection(section);
                setShowAddSection(false);
                setSelectedDressId('');
                setSearchQuery('');
                setSearchResults([]);
              }}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === section
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {SECTION_CONFIG[section].title}
            </button>
          ))}
        </nav>
      </div>

      {/* Section Description + Add Button */}
      <div className="mt-6 sm:flex sm:items-start sm:justify-between">
        <p className="text-sm text-gray-600 max-w-2xl">{config.description}</p>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex-shrink-0">
          {!showAddSection && (
            <button
              onClick={() => setShowAddSection(true)}
              disabled={!canAddMore}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Dress
            </button>
          )}
        </div>
      </div>

      {config.maxItems !== null && entries.length >= config.maxItems && (
        <div className="mt-4 rounded-md bg-yellow-50 p-4">
          <div className="text-sm text-yellow-700">
            Maximum of {config.maxItems} dresses reached. Remove a dress to add a new one.
          </div>
        </div>
      )}

      {/* Add Dress Section */}
      {showAddSection && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-indigo-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Dress to {config.title}</h3>
              <button
                onClick={() => {
                  setShowAddSection(false);
                  setSelectedDressId('');
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search dresses by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {searchResults.map((dress) => (
                    <label
                      key={dress.id}
                      className={`relative flex cursor-pointer rounded-lg border p-3 transition-all hover:shadow-md ${
                        selectedDressId === dress.id
                          ? 'border-indigo-600 ring-2 ring-indigo-600 bg-indigo-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="dress"
                        value={dress.id}
                        checked={selectedDressId === dress.id}
                        onChange={(e) => setSelectedDressId(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-center space-x-3 w-full">
                        <div className="flex-shrink-0">
                          {dress.thumbnailUrl ? (
                            <AuthenticatedImage
                              src={dress.thumbnailUrl}
                              alt={dress.name}
                              className="h-16 w-12 rounded object-cover border border-gray-200"
                              fallback={
                                <div className="h-16 w-12 rounded bg-gray-200 flex items-center justify-center border border-gray-200">
                                  <span className="text-xs text-gray-500">N/A</span>
                                </div>
                              }
                            />
                          ) : (
                            <div className="h-16 w-12 rounded bg-gray-200 flex items-center justify-center border border-gray-200">
                              <span className="text-xs text-gray-500">N/A</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{dress.name}</div>
                          {dress.designer && (
                            <div className="text-xs text-gray-500 mt-0.5">{dress.designer.name}</div>
                          )}
                        </div>
                        {selectedDressId === dress.id && (
                          <div className="absolute top-2 right-2">
                            <div className="h-5 w-5 rounded-full bg-indigo-600 flex items-center justify-center">
                              <div className="h-2.5 w-2.5 rounded-full bg-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end space-x-3 pt-2 border-t">
                  <button
                    onClick={() => {
                      setShowAddSection(false);
                      setSelectedDressId('');
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    disabled={adding}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddDress}
                    disabled={adding || !selectedDressId}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {adding ? 'Adding...' : 'Add to Section'}
                  </button>
                </div>
              </div>
            )}

            {searchResults.length === 0 && searchQuery && !searching && (
              <p className="text-sm text-gray-500 text-center py-4">
                No dresses found. Try a different search term.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No dresses in this section</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add dresses to display them on the homepage.
            </p>
            {!showAddSection && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddSection(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Dress
                </button>
              </div>
            )}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`homepage-dresses-${activeSection}`} isDropDisabled={dragDisabled} direction="vertical">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2 transition-colors duration-200' : ''}`}
                >
                  {entries.map((entry, index) => (
                    <Draggable key={entry.id} draggableId={entry.id} index={index} isDragDisabled={dragDisabled}>
                      {(provided, snapshot) => {
                        const getStyle = (style: any, snapshot: DraggableStateSnapshot) => {
                          if (!snapshot.isDragging) return style;
                          const transform = style?.transform;
                          if (transform) {
                            const match = transform.match(/translate\(.*?,\s*(-?\d+(?:\.\d+)?px)\)/);
                            if (match) {
                              return { ...style, transform: `translate(0px, ${match[1]})` };
                            }
                          }
                          return style;
                        };

                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={getStyle(provided.draggableProps.style, snapshot)}
                            className={`bg-white rounded-lg shadow-sm border transition-all duration-200 ${
                              snapshot.isDragging
                                ? 'shadow-xl border-indigo-400 opacity-95 scale-[1.02] z-50'
                                : 'border-gray-200 hover:shadow-md hover:border-gray-300'
                            }`}
                          >
                            <div className="p-4">
                              <div className="flex items-center space-x-4">
                                {/* Drag Handle */}
                                <div
                                  {...provided.dragHandleProps}
                                  className={`flex-shrink-0 ${dragDisabled ? 'cursor-not-allowed opacity-50' : snapshot.isDragging ? 'cursor-grabbing' : 'cursor-grab hover:bg-gray-100 rounded p-1 transition-colors'}`}
                                  title="Drag to reorder"
                                >
                                  <div className="w-6 h-6 flex flex-col items-center justify-center space-y-1">
                                    <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                    <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                    <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
                                  </div>
                                </div>

                                {/* Dress Thumbnail */}
                                <div className="flex-shrink-0">
                                  {entry.dress.thumbnailUrl ? (
                                    <AuthenticatedImage
                                      src={entry.dress.thumbnailUrl}
                                      alt={entry.dress.name}
                                      className="h-16 w-12 rounded-lg object-cover border border-gray-200"
                                      fallback={
                                        <div className="h-16 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                          <span className="text-xs text-gray-500">N/A</span>
                                        </div>
                                      }
                                    />
                                  ) : (
                                    <div className="h-16 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                      <span className="text-xs text-gray-500">N/A</span>
                                    </div>
                                  )}
                                </div>

                                {/* Dress Info */}
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-medium text-gray-900">{entry.dress.name}</h3>
                                  {entry.dress.designer && (
                                    <p className="text-xs text-gray-500 mt-0.5">{entry.dress.designer.name}</p>
                                  )}
                                  <div className="mt-1 flex items-center space-x-3 text-xs text-gray-500">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100">
                                      Position: {index + 1} of {entries.length}
                                    </span>
                                    {!entry.dress.isActive && (
                                      <span className="text-red-600 font-medium">Dress is inactive</span>
                                    )}
                                  </div>
                                </div>

                                {/* Remove Button */}
                                <div className="flex-shrink-0">
                                  <button
                                    onClick={() => handleRemove(entry)}
                                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                                  >
                                    <TrashIcon className="h-3 w-3 mr-1" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default HomepageDressesManagement;
